import { alpha, Box, Stack, type Theme } from "@mui/material";
import { SUPPORT_TELEPHONE, SUPPORT_WEBFORM_URL } from "@src/appV2/ContactCBHInfo/constants";

import { Button } from "../../components/Button";
import { Text } from "../../components/Text";
import { getBackgroundMuted, getBorderBase, getMutedText, getStrongText } from "../themeHelpers";

function getInvertedText(theme: Theme) {
  return theme.palette.base?.inverted?.base ?? theme.palette.background.paper;
}

function getSectionHeadingSx(theme: Theme, isMobile: boolean) {
  return {
    fontFamily: theme.typography.body1.fontFamily,
    fontSize: isMobile ? theme.typography.h6.fontSize : theme.typography.h5.fontSize,
    lineHeight: 1.2,
    color: getStrongText(theme),
  };
}

interface HelpCenterSupportFooterProps {
  isMobile: boolean;
}

export function HelpCenterSupportFooter(props: HelpCenterSupportFooterProps) {
  const { isMobile } = props;

  return (
    <Stack
      spacing={2}
      sx={(theme) => ({
        alignItems: "center",
        justifyContent: "center",
        paddingTop: theme.spacing(6),
        paddingRight: theme.spacing(4),
        paddingBottom: theme.spacing(6),
        paddingLeft: theme.spacing(4),
        borderRadius: theme.borderRadius?.xLarge,
        backgroundColor: getBackgroundMuted(theme),
        border: `1px solid ${getBorderBase(theme)}`,
      })}
    >
      <Text
        bold
        variant="body1"
        sx={(theme) => ({
          ...getSectionHeadingSx(theme, isMobile),
          textAlign: "center",
        })}
      >
        Can&apos;t find what you&apos;re looking for?
      </Text>
      <Text variant="body2" sx={(theme) => ({ color: getMutedText(theme), textAlign: "center" })}>
        Reach support directly and we&apos;ll help you right away.
      </Text>
      <Box
        sx={(theme) => ({
          display: "grid",
          width: "100%",
          maxWidth: "26rem",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: theme.spacing(2),
        })}
      >
        <Button
          fullWidth
          href={`tel:${SUPPORT_TELEPHONE}`}
          size="large"
          sx={(theme) => ({
            backgroundColor: theme.palette.base?.secondary,
            color: getInvertedText(theme),
            borderColor: theme.palette.base?.secondary,
            "&:hover": {
              backgroundColor: alpha(theme.palette.base?.secondary ?? getStrongText(theme), 0.9),
              borderColor: alpha(theme.palette.base?.secondary ?? getStrongText(theme), 0.9),
            },
          })}
          variant="secondary"
        >
          Call support
        </Button>
        <Button fullWidth component="a" href={SUPPORT_WEBFORM_URL} size="large" variant="outlined">
          Submit request
        </Button>
      </Box>
    </Stack>
  );
}
